import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { adminResetUserPassword, api, startImpersonation } from '../lib/api';
import { hasRole, useHasPerm } from '../lib/auth-store';
import { CreateUserDialog } from '../components/members/create-user-dialog';

type UserRow = {
  id: string;
  email: string;
  username: string | null;
  fullName: string | null;
  status: string;
  kycStatus: string;
  createdAt: string;
};

export function MembersPage() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [kycFilter, setKycFilter] = useState(''); // '' = all
  const [activatedFilter, setActivatedFilter] = useState(''); // '' = all, 'yes' | 'no'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);
  const canResetPassword = useHasPerm('user.password.reset');
  // QoL Slice 4 — only super_admin can impersonate. Other roles never see the
  // option (the backend also enforces it via requireRole('super_admin')).
  const canImpersonate = hasRole('super_admin');
  const showKebab = canResetPassword || canImpersonate;

  async function handleImpersonate(userId: string) {
    setImpersonateError(null);
    try {
      const { url } = await startImpersonation(userId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } }).response?.data;
      setImpersonateError(
        data?.error === 'cannot_impersonate_super_admin'
          ? 'Cannot impersonate a super-admin.'
          : data?.error === 'target_not_active'
            ? 'Target user is not active.'
            : data?.message ?? data?.error ?? 'Could not start impersonation.',
      );
    }
  }

  // Tiny debounce instead of pulling in lodash.
  if (search !== debounced) {
    setTimeout(() => setDebounced(search), 200);
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', debounced, kycFilter, activatedFilter, page, pageSize],
    queryFn: async () => {
      const res = await api.get<{
        rows: UserRow[];
        total: number;
        page: number;
        pageSize: number;
      }>('/users', {
        params: {
          search: debounced || undefined,
          kycStatus: kycFilter || undefined,
          activated: activatedFilter || undefined,
          page,
          pageSize,
        },
      });
      return res.data;
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Members</CardTitle>
          <Button size="sm" onClick={() => setCreateOpen(true)}>+ New User</Button>
        </CardHeader>
        <CardContent className="flex items-end gap-3">
          <Input
            placeholder="Search by email, username, name, or referral code…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="max-w-md"
          />
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">KYC</label>
            <Select
              value={kycFilter || '__all__'}
              onValueChange={(v) => {
                setKycFilter(v === '__all__' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All KYC</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="none">Not submitted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Activation</label>
            <Select
              value={activatedFilter || '__all__'}
              onValueChange={(v) => {
                setActivatedFilter(v === '__all__' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9 w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All members</SelectItem>
                <SelectItem value="yes">Activated (has slots)</SelectItem>
                <SelectItem value="no">Not activated (no slots)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Per page</label>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9 w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ResetPasswordDialog target={resetTarget} onClose={() => setResetTarget(null)} />
      {impersonateError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {impersonateError}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {data && (
              <span>
                Members{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  ({data.total} total · page {page})
                </span>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : error ? (
            <p className="p-6 text-sm text-destructive">Failed to load members.</p>
          ) : !data?.rows.length ? (
            <p className="p-6 text-sm text-muted-foreground">No members match these filters.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Username</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">KYC</th>
                  {showKebab && <th className="px-4 py-2 w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((u) => (
                  <tr key={u.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2">
                      <Link to={`/members/${u.id}`} className="text-foreground hover:underline">
                        {u.email}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      {u.username || <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-2">{u.fullName || ''}</td>
                    <td className="px-4 py-2">
                      <Badge variant={u.status === 'active' ? 'success' : 'muted'}>
                        {u.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={u.kycStatus === 'verified' ? 'success' : 'warning'}>
                        {u.kycStatus}
                      </Badge>
                    </td>
                    {showKebab && (
                      <td className="px-4 py-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost">⋯</Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canImpersonate && (
                              <DropdownMenuItem onSelect={() => void handleImpersonate(u.id)}>
                                Log in as user
                              </DropdownMenuItem>
                            )}
                            {canResetPassword && (
                              <DropdownMenuItem onSelect={() => setResetTarget(u)}>
                                Reset password…
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data && data.total > data.pageSize && (
            <div className="flex justify-end gap-2 p-3">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="text-sm px-2 py-1 rounded border disabled:opacity-50"
              >
                Prev
              </button>
              <button
                disabled={page * data.pageSize >= data.total}
                onClick={() => setPage((p) => p + 1)}
                className="text-sm px-2 py-1 rounded border disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * QoL Slice 2 — confirm-then-reveal flow. Confirm asks the admin to commit;
 * after success the dialog flips to a one-time temp-password display with a
 * copy button. The plaintext is shown ONLY here; closing the dialog drops it.
 */
function ResetPasswordDialog({ target, onClose }: { target: UserRow | null; onClose: () => void }) {
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mut = useMutation({
    mutationFn: () => adminResetUserPassword(target!.id),
    meta: { successMessage: 'Password reset.' },
    onSuccess: (res) => {
      setTempPassword(res.tempPassword);
      setErrMsg(null);
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      setErrMsg(code?.message ?? code?.error ?? 'Failed to reset password.');
    },
  });

  function close() {
    setTempPassword(null);
    setErrMsg(null);
    setCopied(false);
    onClose();
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o && !mut.isPending) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
        </DialogHeader>

        {!tempPassword ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This will generate a new temp password for{' '}
              <span className="font-medium text-foreground">{target?.email}</span>. They will be
              forced to change it on next login.
            </p>
            {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={mut.isPending}>Cancel</Button>
              <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
                {mut.isPending ? 'Generating…' : 'Reset password'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm">
              Temp password for <span className="font-medium">{target?.email}</span>. Read this
              back to the user. <span className="font-medium text-destructive">It will not be shown again.</span>
            </p>
            <div className="rounded-md border bg-muted/30 p-3 font-mono text-lg tracking-widest text-center select-all">
              {tempPassword}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(tempPassword).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  });
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button onClick={close}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

